import jsPDF from "jspdf";

export type PdfColumn<T> = {
  key: keyof T;
  header?: string;
  label?: string;
};

type DownloadReportPdfOptions<
  T extends Record<string, unknown>,
> = {
  title: string;
  date: string;
  rows: T[];
  columns: PdfColumn<T>[];
  fileName: string;
};

const safeText = (value: unknown): string =>
  String(value ?? "")
    .replace(/[\r\n]+/g, " ")
    .replace(/[^\x20-\x7E]/g, " ")
    .trim();

const getColumnHeader = <T>(
  column: PdfColumn<T>,
): string => {
  const header = safeText(column.header);
  if (header) {
    return header;
  }

  const label = safeText(column.label);
  if (label) {
    return label;
  }

  return String(column.key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const wrapText = (
  doc: jsPDF,
  value: string,
  maxWidth: number,
  maxLines = 2,
): string[] => {
  const text = value || "-";

  const lines = doc.splitTextToSize(
    text,
    Math.max(maxWidth, 8),
  ) as string[];

  if (lines.length <= maxLines) {
    return lines;
  }

  const first = lines[0];
  let second = lines[1] ?? "";

  if (second.length > 4) {
    second =
      second.slice(
        0,
        Math.max(1, second.length - 3),
      ) + "...";
  }

  return [first, second];
};

const getColumnWeights = <T>(
  columns: PdfColumn<T>[],
): number[] =>
  columns.map((column) => {
    switch (String(column.key)) {
      case "personName":
        return 3.2;
      case "shift":
        return 2.6;
      case "hoursStatus":
        return 2;
      case "inTime":
      case "outTime":
        return 1.6;
      case "hours":
      case "overtime":
        return 1.5;
      case "status":
        return 1.1;
      case "personId":
        return 1.2;
      default:
        return Math.max(
          getColumnHeader(column).length,
          1,
        );
    }
  });

export const downloadReportPdf = <
  T extends Record<string, unknown>,
>({
  title,
  date,
  rows,
  columns,
  fileName,
}: DownloadReportPdfOptions<T>): void => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  const pageWidth =
    doc.internal.pageSize.getWidth();

  const pageHeight =
    doc.internal.pageSize.getHeight();

  const margin = 10;
  const tableWidth =
    pageWidth - margin * 2;

  const tableTop = 31;
  const headerHeight = 10;
  const minRowHeight = 7;
  const bottomMargin = 12;

  if (!columns.length) {
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(
      safeText(title),
      margin,
      12,
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(
      `Date: ${safeText(date)}    Employees: ${rows.length}`,
      margin,
      19,
    );

    doc.text(
      "No columns configured.",
      margin,
      35,
    );

    doc.save(fileName);
    return;
  }

  const weights =
    getColumnWeights(columns);

  const totalWeight =
    weights.reduce(
      (sum, weight) => sum + weight,
      0,
    );

  const widths = weights.map(
    (weight) =>
      tableWidth *
      (weight / totalWeight),
  );

  const drawPageTitle = (
    continued = false,
  ) => {
    doc.setTextColor(0, 0, 0);
    doc.setFont(
      "helvetica",
      "bold",
    );
    doc.setFontSize(14);

    doc.text(
      continued
        ? `${safeText(title)} (Continued)`
        : safeText(title),
      margin,
      12,
    );

    doc.setFont(
      "helvetica",
      "normal",
    );
    doc.setFontSize(9);

    doc.text(
      `Date: ${safeText(date)}    Employees: ${rows.length}`,
      margin,
      19,
    );
  };

  const drawHeader = (y: number) => {
    let x = margin;

    columns.forEach(
      (column, index) => {
        const width = widths[index];
        const header =
          getColumnHeader(column);

        /*
         * IMPORTANT:
         * Fill, border, and text are deliberately separated.
         * This prevents fill/text state leakage in jsPDF.
         */
        doc.setFillColor(
          245,
          245,
          245,
        );
        doc.rect(
          x,
          y,
          width,
          headerHeight,
          "F",
        );

        doc.setDrawColor(
          150,
          150,
          150,
        );
        doc.rect(
          x,
          y,
          width,
          headerHeight,
          "S",
        );

        /*
         * Explicitly reset text state for EVERY cell.
         */
        doc.setTextColor(
          0,
          0,
          0,
        );
        doc.setFont(
          "helvetica",
          "bold",
        );
        doc.setFontSize(7.5);

        const lines = wrapText(
          doc,
          header,
          width - 4,
          2,
        );

        if (lines.length === 1) {
          doc.text(
            safeText(lines[0]),
            x + 2,
            y + 6.2,
            {
              baseline:
                "alphabetic",
            },
          );
        } else {
          doc.text(
            safeText(lines[0]),
            x + 2,
            y + 4.2,
            {
              baseline:
                "alphabetic",
            },
          );

          doc.text(
            safeText(lines[1]),
            x + 2,
            y + 7.4,
            {
              baseline:
                "alphabetic",
            },
          );
        }

        x += width;
      },
    );

    doc.setTextColor(0, 0, 0);
  };

  const getRowHeight = (
    row: T,
  ): number => {
    const counts = columns.map(
      (column, index) =>
        wrapText(
          doc,
          safeText(
            row[column.key],
          ),
          widths[index] - 4,
          4,
        ).length,
    );

    return Math.max(
      minRowHeight,
      ...counts.map(
        (count) =>
          count * 3.4 + 2.6,
      ),
    );
  };

  const drawRow = (
    row: T,
    y: number,
  ): number => {
    const rowHeight =
      getRowHeight(row);

    let x = margin;

    columns.forEach(
      (column, index) => {
        const width = widths[index];

        const lines = wrapText(
          doc,
          safeText(
            row[column.key],
          ),
          width - 4,
          4,
        );

        doc.setDrawColor(
          220,
          220,
          220,
        );
        doc.rect(
          x,
          y,
          width,
          rowHeight,
          "S",
        );

        doc.setTextColor(
          20,
          20,
          20,
        );
        doc.setFont(
          "helvetica",
          "normal",
        );
        doc.setFontSize(7);

        let textY = y + 4.6;

        lines.forEach(
          (line) => {
            doc.text(
              safeText(line),
              x + 2,
              textY,
              {
                baseline:
                  "alphabetic",
              },
            );

            textY += 3.4;
          },
        );

        x += width;
      },
    );

    return y + rowHeight;
  };

  let y = tableTop;

  drawPageTitle(false);
  drawHeader(y);
  y += headerHeight;

  if (!rows.length) {
    doc.setDrawColor(
      220,
      220,
      220,
    );

    doc.rect(
      margin,
      y,
      tableWidth,
      14,
      "S",
    );

    doc.setTextColor(
      80,
      80,
      80,
    );
    doc.setFont(
      "helvetica",
      "normal",
    );
    doc.setFontSize(9);

    doc.text(
      "No records found.",
      pageWidth / 2,
      y + 8.5,
      {
        align: "center",
      },
    );
  } else {
    rows.forEach(
      (row) => {
        const rowHeight =
          getRowHeight(row);

        if (
          y + rowHeight >
          pageHeight -
            bottomMargin
        ) {
          doc.addPage();

          y = tableTop;

          drawPageTitle(
            true,
          );
          drawHeader(y);

          y += headerHeight;
        }

        y = drawRow(
          row,
          y,
        );
      },
    );
  }

  const totalPages =
    doc.getNumberOfPages();

  for (
    let page = 1;
    page <= totalPages;
    page += 1
  ) {
    doc.setPage(page);

    doc.setTextColor(
      100,
      100,
      100,
    );
    doc.setFont(
      "helvetica",
      "normal",
    );
    doc.setFontSize(7);

    doc.text(
      `Page ${page} of ${totalPages}`,
      pageWidth - margin,
      pageHeight - 5,
      {
        align: "right",
      },
    );
  }

  doc.save(fileName);
};
